import React, {
  useRef,
  useEffect,
  forwardRef,
  useContext,
  ReactElement,
  createContext,
  HTMLAttributes,
} from 'react';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import { useTheme, styled } from '@mui/material/styles';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';

const LISTBOX_PADDING = 8; // px

const renderRow = (props: ListChildComponentProps) => {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  if (Object.prototype.hasOwnProperty.call(dataSet, 'group')) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  const { key, ...optionProps } = dataSet[0];

  return (
    <Typography
      key={key}
      component="li"
      {...optionProps}
      noWrap
      style={inlineStyle}
    >
      {dataSet[1]}
    </Typography>
  );
};

const OuterElementContext = createContext({});

// eslint-disable-next-line react/display-name
const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const useResetCache = (data: any) => {
  const ref = useRef<VariableSizeList>(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
};

// Adapter for react-window
const ListboxComponent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData: ReactElement[] = [];
  (children as ReactElement[]).forEach(
    (item: ReactElement & { children?: ReactElement[] }) => {
      itemData.push(item);
      itemData.push(...(item.children || []));
    }
  );

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child: ReactElement) => {
    if (Object.prototype.hasOwnProperty.call(child, 'group')) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

export interface VirtualizeAutocompleteOption {
  groupLabel: string;
  label: string;
  value: string;
  [key: string]: any; // This allows additional properties to be added
}

interface VirtualizeAutocompleteProps {
  value: any;
  label?: string;
  width?: number;
  disabled?: boolean;
  includeGroupLabel?: boolean;
  handleChange: (event: React.SyntheticEvent, value: any) => void;
  groupedOptions: VirtualizeAutocompleteOption[];
}

const VirtualizeAutocomplete: React.FC<VirtualizeAutocompleteProps> = ({
  value,
  label,
  width = 300,
  handleChange,
  groupedOptions,
  disabled = false,
  includeGroupLabel = false,
}) => (
  <Autocomplete
    id="virtualize-autocomplete"
    size="small"
    sx={{ width: width }}
    disableListWrap
    PopperComponent={StyledPopper}
    ListboxComponent={ListboxComponent}
    options={groupedOptions}
    groupBy={(option) => option.groupLabel ?? ''}
    getOptionLabel={(option) =>
      option.groupLabel && option.label
        ? includeGroupLabel
          ? `${option.groupLabel}: ${option.label}`
          : option.label
        : ''
    }
    renderInput={(params) => (
      <TextField {...params} label={label} value={value ?? ''} />
    )}
    renderOption={(props, option, state) =>
      [props, option.label, state.index] as React.ReactNode
    }
    renderGroup={(params) => params as any}
    onChange={(event: React.SyntheticEvent, value: any) => {
      handleChange(event, value);
    }}
    value={value ?? ''}
    disabled={disabled}
  />
);

export default VirtualizeAutocomplete;
